import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import Navbar from './Navbar';
import Footer from './Footer';
import SeoHead from './SeoHead';
import { Container } from './styled/Container';
import { PageLayout } from './styled/PageLayout';
import { useLocale } from '../context/LocaleContext';
import { getPageBySlug } from '../services/pageServices';

function canonicalFromPath(prefixPathFn, canonicalPath) {
  if (!canonicalPath) return undefined;
  const s = String(canonicalPath).trim();
  if (!s) return undefined;
  const p = s.startsWith('/') ? s : `/${s}`;
  return prefixPathFn(p);
}

export default function ManagedStaticPage({ slug, fallbackTitle = '' }) {
  const { prependPath } = useLocale();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getPageBySlug(slug);
        if (!alive) return;
        setPage(data || null);
      } catch (e) {
        if (!alive) return;
        setError(e.response?.data?.message || e.message || 'Failed to load page');
        setPage(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  const seo = page?.seo || {};
  const title = seo?.title ? String(seo.title) : (page?.title || fallbackTitle || 'CiteMatch');
  const description = seo?.description ? String(seo.description) : undefined;
  const keywords = seo?.keywords ? String(seo.keywords) : undefined;
  const noindex = seo?.robots ? String(seo.robots).toLowerCase().includes('noindex') : false;
  const canonicalPath = canonicalFromPath(prependPath, seo?.canonicalPath);

  const safeHtml = useMemo(() => DOMPurify.sanitize(String(page?.body || '')), [page?.body]);

  return (
    <PageLayout>
      <SeoHead title={title} description={description} keywords={keywords} canonicalPath={canonicalPath} noindex={noindex} />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem', maxWidth: '52rem' }}>
        {loading ? <p style={{ color: 'var(--muted-foreground)' }}>Loading…</p> : null}
        {error ? <p style={{ color: 'var(--crimson)' }}>{error}</p> : null}
        {page ? (
          <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
        ) : !loading ? (
          <h1 style={{ margin: 0 }}>{fallbackTitle || 'Page'}</h1>
        ) : null}
      </Container>
      <Footer />
    </PageLayout>
  );
}

